import {
  MODULE_KEY,
  type GlobalSearchResultsDto,
  type ModuleKey,
  type SearchResultItemDto,
} from '@gty/common';

import { MODULE_REGISTRY } from '../../app/module-registry';
import type { IconName } from '../../ui/icons';

/**
 * The pure half of global search: how the nine server groups become one
 * keyboard-navigable list, and where each row points.
 *
 * No React, no fetching. Everything here is a total function of the DTO, which
 * is what lets the keyboard behaviour be reasoned about (and tested) without a
 * network or a router.
 */

export type SearchGroupKey =
  | 'leads'
  | 'customers'
  | 'bookings'
  | 'tours'
  | 'invoices'
  | 'payments'
  | 'creditNotes'
  | 'vouchers'
  | 'expenses';

/**
 * THE ORDER IS FIXED AND IT IS LOAD-BEARING.
 *
 * ArrowDown walks the rendered list top to bottom, so the flattening order and
 * the rendering order have to be the same order — otherwise the highlight
 * visibly jumps around. Both read this array, so they cannot disagree.
 *
 * Sales-funnel order first (a lead becomes a customer, who makes a booking,
 * against a tour), then the money documents in the order they are raised for
 * one booking (invoice, payment, credit note, voucher), then expenses last —
 * which also matches the sidebar's Sales → Finance → Operations grouping.
 */
export const SEARCH_GROUPS: readonly {
  key: SearchGroupKey;
  label: string;
  basePath: string;
  icon: IconName;
}[] = [
  { key: 'leads', label: 'Leads', basePath: pathOf(MODULE_KEY.LEAD_CRM, '/leads'), icon: 'leads' },
  {
    key: 'customers',
    label: 'Customers',
    basePath: pathOf(MODULE_KEY.CUSTOMER_CRM, '/customers'),
    icon: 'customers',
  },
  {
    key: 'bookings',
    label: 'Bookings',
    basePath: pathOf(MODULE_KEY.BOOKING_MANAGEMENT, '/bookings'),
    icon: 'bookings',
  },
  { key: 'tours', label: 'Tours', basePath: pathOf(MODULE_KEY.TOUR_MASTER, '/tours'), icon: 'tours' },
  {
    key: 'invoices',
    label: 'Invoices',
    basePath: pathOf(MODULE_KEY.INVOICE_GENERATOR, '/invoices'),
    icon: 'invoices',
  },
  {
    key: 'payments',
    label: 'Payments & Receipts',
    basePath: pathOf(MODULE_KEY.PAYMENTS_RECEIPTS, '/payments'),
    icon: 'payments',
  },
  {
    key: 'creditNotes',
    label: 'Credit Notes',
    basePath: pathOf(MODULE_KEY.CREDIT_NOTES, '/credit-notes'),
    icon: 'credit',
  },
  {
    key: 'vouchers',
    label: 'Vouchers',
    basePath: pathOf(MODULE_KEY.BOOKING_VOUCHER, '/vouchers'),
    icon: 'vouchers',
  },
  {
    key: 'expenses',
    label: 'Expenses',
    basePath: pathOf(MODULE_KEY.EXPENSE_TRACKING, '/expenses'),
    icon: 'expenses',
  },
] as const;

/**
 * Route prefixes come from the module registry, not from literals typed here.
 *
 * The registry is already the single source of truth for "where does this
 * module live" — the router and the sidebar are both generated from it — and a
 * search result that navigates to a hand-copied path is a fifth copy waiting to
 * go stale the day someone remaps `/tours`. The fallback exists only so a
 * registry edit can never crash the shell at import time.
 */
function pathOf(module: ModuleKey, fallback: string): string {
  return MODULE_REGISTRY.find((entry) => entry.module === module)?.path ?? fallback;
}

/** One row in the flattened list, carrying everything the UI and Enter need. */
export interface SearchHit {
  group: SearchGroupKey;
  item: SearchResultItemDto;
  /** In-app route. Built from `id`. */
  to: string;
  /** DOM id, for `aria-activedescendant`. */
  domId: string;
}

/**
 * NAVIGATE BY `id`. NEVER BY `ref`.
 *
 * `/bookings/new` is a literal route that React Router ranks ABOVE the
 * `:bookingId` param, so a booking whose ref is the string "new" would route to
 * the create-booking wizard instead of to itself. `ref` is user-facing free
 * text with a uniqueness constraint — it is not a route-safe key, and the DTO
 * says so in as many words. `id` is a uuid and cannot collide with any literal
 * segment.
 *
 * `encodeURIComponent` is belt-and-braces on a uuid, but it costs nothing and
 * survives the day ids stop being uuids.
 */
export function routeForHit(basePath: string, item: SearchResultItemDto): string {
  return `${basePath}/${encodeURIComponent(item.id)}`;
}

/**
 * Flatten the four groups into the single list the keyboard walks.
 *
 * Empty groups contribute nothing — no header, no index — so ArrowDown never
 * stops on a heading and the highlighted index always addresses a real row.
 */
export function flattenSearchResults(results: GlobalSearchResultsDto | undefined): SearchHit[] {
  if (results === undefined) return [];

  return SEARCH_GROUPS.flatMap(({ key, basePath }) =>
    results[key].map((item) => ({
      group: key,
      item,
      to: routeForHit(basePath, item),
      domId: `global-search-option-${key}-${item.id}`,
    })),
  );
}

/**
 * Does this response have anything to show?
 *
 * Reads the four arrays rather than trusting `totalCount`: the count is a
 * server-computed convenience and the arrays are what gets rendered. If they
 * ever disagree, the arrays win, because they are the thing the user sees.
 */
export function hasAnyResult(results: GlobalSearchResultsDto | undefined): boolean {
  if (results === undefined) return false;
  return SEARCH_GROUPS.some(({ key }) => results[key].length > 0);
}
